import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { Bell, Stamp, XCircle, CheckCircle2 } from 'lucide-react';
import { splitNotificationMessage } from '@/lib/desktopNotification';

/**
 * 새 알림 팝업.
 *
 * Navbar 가 SignalR 로 알림을 받으면 'showToast' 이벤트를 쏘는데,
 * 정작 그 이벤트를 듣는 곳이 없어 지금까지 아무것도 뜨지 않았다.
 * 이 컴포넌트가 그 이벤트를 받아 화면 우측 상단에 작은 팝업으로 띄운다.
 *
 * 이벤트 발생부(SignalR 연결)와 분리해 둔 이유:
 *   SignalR 효과는 의존성이 비어 있어야(연결이 매번 끊겼다 붙지 않아야) 하는데,
 *   팝업은 navigate·notification 을 참조해야 한다. 둘을 한 곳에 두면 서로를 방해한다.
 */

/** 알림 종류에 맞는 아이콘. 문구 앞머리([결재요청] 같은)로 고른다. */
const iconFor = (kind) => {
  if (kind === '반려') return <XCircle size={18} className="text-red-500" />;
  if (kind === '결재요청') return <Stamp size={18} className="text-amber-500" />;
  if (kind === '결재완료') return <CheckCircle2 size={18} className="text-emerald-500" />;
  return <Bell size={18} className="text-blue-500" />;
};

const NotificationToaster = () => {
  const navigate = useNavigate();
  const { notification } = AntdApp.useApp();

  useEffect(() => {
    const handleToast = (event) => {
      const detail = event.detail || {};
      const raw = detail.message;
      if (!raw) return;

      const { title, body } = splitNotificationMessage(raw);
      const targetUrl = detail.targetUrl;

      notification.open({
        // 같은 문서의 알림이 연달아 오면 덮어써서 화면이 팝업으로 덮이지 않게 한다.
        key: targetUrl || `noti-${detail.notificationId ?? raw}`,
        message: <span className="text-sm font-bold text-slate-800">{title}</span>,
        description: <span className="text-xs leading-relaxed text-slate-600">{body}</span>,
        icon: iconFor(title),
        placement: 'topRight',
        duration: 6,
        // 상단바(h-16) 아래에서 시작해 알림 아이콘을 가리지 않도록 한다.
        top: 72,
        onClick: targetUrl
          ? () => {
            navigate(targetUrl);
            notification.destroy(targetUrl);
          }
          : undefined,
        className: targetUrl ? 'cursor-pointer' : undefined,
      });
    };

    window.addEventListener('showToast', handleToast);
    return () => window.removeEventListener('showToast', handleToast);
  }, [navigate, notification]);

  return null;
};

export default NotificationToaster;
